<template>
    <div class="mt-4 flex flex-col items-start sm:flex-row sm:items-center sm:justify-between">
        <div
            class="inline-flex w-full sm:ml-auto lg:w-auto rounded bg-slate-200 p-1"
            role="group"
        >
            <button
                v-for="metricWindow in metricsWindows"
                :key="metricWindow.dayRange"
                class="flex grow rounded px-3 py-2 sm:px-4 font-semibold leading-tight transition duration-150 ease-in-out focus:outline-none focus:ring-0 hover:text-foreground"
                :class="{
                    'bg-white text-foreground': filters.dayRange === metricWindow.dayRange,
                }"
                @click.prevent="filters.dayRange = metricWindow.dayRange"
                @click="onGenerateReport(false, false)"
            >
                <span class="mx-auto text-sm capitalize">{{ getFromDictionary(`superview.metricWindow.${metricWindow.title}`) }}</span>
            </button>
        </div>
    </div>
    <div v-if="!state.hasGenerated"></div>

    <div v-else class="mt-4">
        <DiscoverTable
            :paging="paging"
            :creators="creators"
            :is-loading="!state.isLoaded"
            :is-busy="!state.isLoaded"
            :columns="table.columns"
            :enabled-columns="table.enabledColumns"
            :is-selectable="false"
            id-prop="id"
            superview-path="partialview"
            :list-id="list?.id"
            :is-editable="false"
            @enabled-column-change="onEnabledColumnChange"
            @filter-change="onFilterChange"
            @paging-change="onPagingChange"
            @export-creators="onExportCreators"
        >
            <template #header-toolbar>
                <ColumnDisplayManager
                    :available-columns="table.columns"
                    :groups="availableColumnGroups"
                    :model-value="table.enabledColumns"
                    @update:model-value="(newEnabledColumns) => onEnabledColumnChange(newEnabledColumns)"
                />
            </template>

            <template #creator="{ item }">
                <div class="relative flex w-full items-center justify-between">
                    <router-link
                        :to="{
                            path: $route.path,
                            query: {
                                ...$route.query,
                                modal: 'creator',
                                platform_id: item.primary.partialview.platform_id,
                                platform_user_id: item.primary.partialview._id,
                            },
                        }"
                        class="align-center flex"
                    >
                        <div class="inline-block rounded-full bg-slate-100 transition duration-200 hover:bg-slate-200">
                            <div class="ml-2 flex pr-3">
                                <div class="my-auto flex text-sm font-medium text-slate-800">
                                    {{ item.primary.partialview.normalized.display_name }}
                                </div>
                            </div>
                        </div>
                    </router-link>
                </div>
            </template>

            <template #creator-small="creator">
                <div v-if="creator.field.id" class="relative flex w-full items-center justify-between">
                    <router-link
                        :to="{
                            path: $route.path,
                            query: {
                                ...$route.query,
                                modal: 'creator',
                                platform_id: creator.field.platform_id,
                                platform_user_id: creator.field.id,
                            },
                        }"
                        class="align-center flex z-0"
                    >
                        <NamePlate
                            class="my-auto"
                            :logo="creator.field.avatar_url"
                            :name="creator.field.name"
                            :display-name="creator.field.display_name"
                            :platform-id="creator.field.platform_id"
                            :platform-user-id="creator.field.id"
                            :is-drawer-enabled="false"
                            :is-interactive="true"
                            :hideName="true"
                        />
                    </router-link>
                </div>
            </template>

            <template #language="{ field }">
                <Tooltip v-if="field" class="inline-block">
                    <template #trigger>
                        <div class="inline-block cursor-help rounded bg-slate-100 px-2 py-1 text-xs font-semibold">
                            <span class="uppercase">{{ field }}</span>
                        </div>
                    </template>
                    <template #default>
                        <div class="whitespace-nowrap text-xs">{{ getLanguageFromCode(field) }}</div>
                    </template>
                </Tooltip>
            </template>

            <template #country="{ field }">
                <Tooltip v-if="field" class="inline-block">
                    <template #trigger>
                        <div class="inline-block cursor-help rounded bg-slate-100 px-2 py-1 text-xs font-semibold">
                            <span class="uppercase">{{ field }}</span>
                        </div>
                    </template>
                    <template #default>
                        <div class="whitespace-nowrap text-xs">{{ getCountryFromCode(field) }}</div>
                    </template>
                </Tooltip>
            </template>

            <template #platform-link="{ field }">
                <TableCellPlatformLink
                    v-if="field"
                    :platform-id="field?.platform_id"
                    :platform-user-id="field?.id"
                    :platform-user-name="field?.name"
                    :link="field?.link"
                />
            </template>

            <template #email="{ field }">
                <EmailUnlock
                    v-if="field.platform_id && field.platform_user_id"
                    :platform-id="field.platform_id"
                    :platform-user-id="field.platform_user_id"
                />
            </template>

        </DiscoverTable>
    </div>
</template>

<script>
import { MEDIA_CDN_BASE } from '@/utils/media'
import _ from 'lodash'
import numeral from 'numeral'
import { mapGetters } from 'vuex'
import { nextTick } from 'vue'
import reportsAPI from '@/utils/api/api.reports'
import NamePlate from '@/components/plates/NamePlate.vue'
import Tooltip from '@/components/Tooltip.vue'
import DiscoverTable from '@/partials/tables/DiscoverTable.vue'
import ColumnDisplayManager from '@/partials/options/ColumnDisplayManager.vue'
import TableCellPlatformLink from '@/partials/tables/creator-lists/TableCellPlatformLink.vue'
import EmailUnlock from '@/components/unlocks/EmailUnlock.vue'

export default {
    name: 'ListReportCombined',

    components: {
        NamePlate,
        Tooltip,
        DiscoverTable,
        ColumnDisplayManager,
        TableCellPlatformLink,
        EmailUnlock,
    },

    props: {
        list: {
            type: Object,
        },
        fixedDayRange: {
            type: Boolean,
        },
        searchText: {
            type: String,
        },
    },

    emits: [],

    data() {
        return {
            creators: [],
            state: {
                hasGenerated: false,
                isLoaded: true,
                isBusy: false,
            },
            paging: {
                pages: 1,
                perPage: 25,
                currentPage: 1,
                totalPages: 1,
                sort: [
                    {
                        field: 'partialview.normalized.name',
                        direction: 'asc',
                        source: 'default',
                    },
                ],
            },
            filters: {
                dayRange: 30,
            },
            metricsWindows: [
                { dayRange: 30, title: 'day_30' },
                { dayRange: 90, title: 'day_90' },
                { dayRange: 365, title: 'day_365' },
            ],
            table: {
                enabledColumns: ['creator'],
                columns: [],
            },

            columnGroups: [
                { id: 'basic', label: 'Basic', platforms: ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram'] },
                { id: 'twitch', label: 'Twitch', platforms: ['twitch'] },
                { id: 'youtube', label: 'YouTube', platforms: ['youtube'] },
                { id: 'tiktok', label: 'TikTok', platforms: ['tiktok'] },
                { id: 'twitter', label: 'X', platforms: ['twitter'] },
                { id: 'instagram', label: 'Instagram', platforms: ['instagram'] },
            ],
        }
    },
    computed: {
        ...mapGetters(['getFromDictionary', 'isSfStaff', 'getLanguageFromCode', 'getCountryFromCode', 'preferences']),
        visibleColumns() {
            return _.filter(this.table.columns, (column) => this.table.enabledColumns.includes(column.id))
        },
        sortLookup() {
            return _.reduce(
                this.paging.sort,
                (result, sortEntry) => {
                    result[sortEntry.field] = sortEntry.direction
                    return result
                },
                {},
            )
        },

        availableColumnGroups() {
            return this.columnGroups
        },
    },

    watch: {
        list() {
            this.reset()
        },
        fixedDayRange() {
            this.reset()
        },
        searchText() {
            this.paging.currentPage = 1
            this.debounceGenerate()
        },
        'filters.dayRange': function() {
            this.createColumns() // Regenerate columns when day range changes
            this.debounceGenerate()
        },
    },

    created() {
        this.createColumns()
        this.reset()
    },

    methods: {
        // Helper function to get language/country from first available platform
        getFirstAvailablePlatformValue(item, field) {
            const platformOrder = ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']

            for (const platformKey of platformOrder) {
                const platformData = _.get(item, `platforms.${platformKey}[0]`)
                if (platformData) {
                    const value = _.get(platformData, `partialview.normalized.${field}`)
                    if (value) {
                        return value
                    }
                }
            }
            return ''
        },

        // Helper function to get platform data for email unlock component
        getFirstAvailablePlatformData(item, field) {
            const platformOrder = ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']

            for (const platformKey of platformOrder) {
                const platformData = _.get(item, `platforms.${platformKey}[0]`)
                if (platformData) {
                    return {
                        platform_id: platformKey,
                        platform_user_id: platformData.partialview._id,
                    }
                }
            }
            return { platform_id: null, platform_user_id: null }
        },

        formatGenderValue(value) {
            if (!value) {
                return ''
            }

            return _.startCase(value)
        },

        formatEngagementRate(value, emptyValue = '-') {
            if (value === null || value === undefined || value === '') {
                return emptyValue
            }

            return numeral(value).format('0.0%')
        },

        getDefaultExportColumns() {
            const exportColumnIds = new Set(this.table.enabledColumns)

            return _.filter(this.table.columns, (column) => exportColumnIds.has(column.id))
        },

        createColumns() {
            let tableColumns = []
            let platforms = [
                {
                    key: 'twitch',
                    label: 'Twitch',
                    contentType: 'streams',
                    metrics: [
                        {
                            field: 'metrics.streams.content_count',
                            label: 'Streams',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Stream Count',
                                content: 'The total number of streams during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.streams.avg_viewers',
                            label: 'Avg Viewers',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Average Concurrent Viewers',
                                content: 'The average number of viewers you would expect to see in this creator\'s stream at any point within the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.streams.peak_viewers',
                            label: 'Peak Viewers',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Peak Concurrent Viewers',
                                content: 'The maximum number of viewers that were in this creator\'s stream at any point within the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.streams.avg_chatters',
                            label: 'Avg Chatters',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Average Concurrent Chatters',
                                content: 'The average number of chatters you would expect to see in this creator\'s stream at any point within the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.streams.sum_duration_seconds',
                            label: 'Hours Streamed',
                            formatter: 'formatHours',
                            tooltip: {
                                title: 'Hours Streamed',
                                content: 'The total number of hours streamed during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.streams.avg_engagement_rate',
                            label: 'Stream Engagement Rate',
                            formatter: 'formatEngagementRate',
                            tooltip: {
                                title: 'Average Engagement Rate',
                                content: 'The average percentage of viewers that decide to interact with the creator\'s stream.'
                            }
                        },
                        {
                            field: 'partialview.normalized.followers',
                            label: 'Followers',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Followers',
                                content: 'The channel\'s current number of followers.'
                            }
                        },
                    ]
                },
                {
                    key: 'youtube',
                    label: 'YouTube',
                    contentType: 'videos',
                    metrics: [
                        {
                            field: 'metrics.videos.content_count',
                            label: 'Videos',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Video Count',
                                content: 'The total number of videos published during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.videos.avg_views',
                            label: 'Avg Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Average Views',
                                content: 'The average number of views per video during the period.'
                            }
                        },
                        {
                            field: 'metrics.videos.sum_views',
                            label: 'Total Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Total Views',
                                content: 'The total number of views for videos during the period.'
                            }
                        },
                        {
                            field: 'metrics.videos.peak_views',
                            label: 'Peak Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Peak Views',
                                content: 'The peak number of views for videos during the period.'
                            }
                        },
                        {
                            field: 'metrics.videos.sum_likes',
                            label: 'Video Likes',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Likes',
                                content: 'The total number of likes during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.videos.sum_comments',
                            label: 'Comments',
                            formatter: 'formatWholeNumberWithUnit',
                            emptyValue: '',
                            tooltip: {
                                title: 'Comments',
                                content: 'The total number of comments during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.videos.avg_engagement_rate',
                            label: 'Video Engagement Rate',
                            formatter: 'formatEngagementRate',
                            tooltip: {
                                title: 'Average Engagement Rate',
                                content: 'The average percentage of viewers that decide to interact with the creator\'s videos.'
                            }
                        },
                        {
                            field: 'partialview.normalized.followers',
                            label: 'Subscribers',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Subscribers',
                                content: 'The channel\'s current number of subscribers.'
                            }
                        },
                    ]
                },
                {
                    key: 'tiktok',
                    label: 'TikTok',
                    contentType: 'videos',
                    metrics: [
                        {
                            field: 'metrics.videos.content_count',
                            label: 'Videos',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Video Count',
                                content: 'The total number of videos published during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.videos.avg_views',
                            label: 'Avg Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Average Views',
                                content: 'The average number of views per video during the period.'
                            }
                        },
                        {
                            field: 'metrics.videos.sum_views',
                            label: 'Total Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Total Views',
                                content: 'The total number of views for videos during the period.'
                            }
                        },
                        {
                            field: 'metrics.videos.peak_views',
                            label: 'Peak Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Peak Views',
                                content: 'The peak number of views for videos during the period.'
                            }
                        },
                        {
                            field: 'metrics.videos.sum_likes',
                            label: 'Video Likes',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Likes',
                                content: 'The total number of likes during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.videos.sum_comments',
                            label: 'Video Comments',
                            formatter: 'formatWholeNumberWithUnit',
                            emptyValue: '',
                            tooltip: {
                                title: 'Comments',
                                content: 'The total number of comments during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.videos.sum_shares',
                            label: 'Video Shares',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Shares',
                                content: 'The total number of shares during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.videos.avg_engagement_rate',
                            label: 'Video Engagement Rate',
                            formatter: 'formatEngagementRate',
                            tooltip: {
                                title: 'Average Engagement Rate',
                                content: 'The average percentage of viewers that decide to interact with the creator\'s videos.'
                            }
                        },
                        {
                            field: 'partialview.normalized.followers',
                            label: 'Followers',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Followers',
                                content: 'The channel\'s current number of followers.'
                            }
                        },
                    ]
                },
                {
                    key: 'twitter',
                    label: 'X',
                    contentType: 'posts',
                    metrics: [
                        {
                            field: 'metrics.posts.content_count',
                            label: 'Posts',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Post Count',
                                content: 'The total number of posts published during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.posts.avg_views',
                            label: 'Avg Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Average Views',
                                content: 'The average number of views per post during the period.'
                            }
                        },
                        {
                            field: 'metrics.posts.sum_views',
                            label: 'Total Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Total Views',
                                content: 'The total number of views for posts during the period.'
                            }
                        },
                        {
                            field: 'metrics.posts.peak_views',
                            label: 'Peak Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Peak Views',
                                content: 'The peak number of views for posts during the period.'
                            }
                        },
                        {
                            field: 'metrics.posts.sum_likes',
                            label: 'Post Likes',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Likes',
                                content: 'The total number of likes during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.posts.sum_shares',
                            label: 'Post Reposts',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Reposts',
                                content: 'The total number of reposts during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.posts.sum_comments',
                            label: 'Post Comments',
                            formatter: 'formatWholeNumberWithUnit',
                            emptyValue: '',
                            tooltip: {
                                title: 'Comments',
                                content: 'The total number of comments during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.posts.sum_quotes',
                            label: 'Post Quotes',
                            formatter: 'formatWholeNumberWithUnit',
                            emptyValue: '',
                            tooltip: {
                                title: 'Quotes',
                                content: 'The total number of quotes during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.posts.avg_engagement_rate',
                            label: 'Post Engagement Rate',
                            formatter: 'formatEngagementRate',
                            tooltip: {
                                title: 'Average Engagement Rate',
                                content: 'The average percentage of viewers that decide to interact with the creator\'s posts.'
                            }
                        },
                        {
                            field: 'partialview.normalized.followers',
                            label: 'Followers',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Followers',
                                content: 'The channel\'s current number of followers.'
                            }
                        },
                    ]
                },
                {
                    key: 'instagram',
                    label: 'Instagram',
                    contentType: 'posts',
                    metrics: [
                        {
                            field: 'metrics.posts.content_count',
                            label: 'Posts',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Post Count',
                                content: 'The total number of posts published during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.posts.avg_views',
                            label: 'Avg Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Average Views',
                                content: 'The average number of views per post during the period.'
                            }
                        },
                        {
                            field: 'metrics.posts.sum_views',
                            label: 'Total Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Total Views',
                                content: 'The total number of views for posts during the period.'
                            }
                        },
                        {
                            field: 'metrics.posts.peak_views',
                            label: 'Peak Views',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Peak Views',
                                content: 'The peak number of views for posts during the period.'
                            }
                        },
                        {
                            field: 'metrics.posts.sum_likes',
                            label: 'Post Likes',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Likes',
                                content: 'The total number of likes during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.posts.sum_comments',
                            label: 'Post Comments',
                            formatter: 'formatWholeNumberWithUnit',
                            emptyValue: '',
                            tooltip: {
                                title: 'Comments',
                                content: 'The total number of comments during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.posts.avg_engagement_rate',
                            label: 'Post Engagement Rate',
                            formatter: 'formatEngagementRate',
                            tooltip: {
                                title: 'Average Engagement Rate',
                                content: 'The average percentage of viewers that decide to interact with the creator\'s posts.'
                            }
                        },
                        {
                            field: 'metrics.videos.sum_likes',
                            label: 'Video Likes',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Likes',
                                content: 'The total number of likes during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.videos.sum_comments',
                            label: 'Video Comments',
                            formatter: 'formatWholeNumberWithUnit',
                            emptyValue: '',
                            tooltip: {
                                title: 'Comments',
                                content: 'The total number of comments during the aggregation window.'
                            }
                        },
                        {
                            field: 'metrics.videos.avg_engagement_rate',
                            label: 'Video Engagement Rate',
                            formatter: 'formatEngagementRate',
                            tooltip: {
                                title: 'Average Engagement Rate',
                                content: 'The average percentage of viewers that decide to interact with the creator\'s videos.'
                            }
                        },
                        {
                            field: 'partialview.normalized.followers',
                            label: 'Followers',
                            formatter: 'formatWholeNumberWithUnit',
                            tooltip: {
                                title: 'Followers',
                                content: 'The channel\'s current number of followers.'
                            }
                        },
                    ]
                },
            ]

            // Get current metric window name for sub labels
            const currentMetricWindow = _.find(this.metricsWindows, window => window.dayRange === this.filters.dayRange)
            const metricWindowName = currentMetricWindow ? this.getFromDictionary(`superview.metricWindow.${currentMetricWindow.title}`) : `${this.filters.dayRange} day`

            // Add primary creator column
            tableColumns.push({
                id: 'creator',
                key: 'creator',
                header: {
                    label: 'Creator',
                    is_sortable: true,
                    is_hideable: false,
                    class: '',
                },
                row: {
                    class: '',
                    slot: 'creator',
                },
                is_sticky: true,
                is_exportable: true,
                exportTransformer: (item) => {
                    return {
                        Creator: this.getFirstAvailablePlatformValue(item, 'display_name'),
                    }
                },
                enabled: () => {
                    return true
                },
                group: 'basic',
            })

            // Add global language column (from first available platform)
            tableColumns.push({
                id: 'global_language',
                key: 'global_language',
                header: {
                    label: 'Language',
                    is_sortable: false,
                    is_hideable: true,
                    class: '',
                },
                row: {
                    class: '',
                    formatter: ({ item }) => {
                        const language = this.getFirstAvailablePlatformValue(item, 'language')

                        return language && this.getLanguageFromCode(language) !== 'Unknown' ? this.getLanguageFromCode(language) : ''
                    },
                },
                is_sticky: false,
                is_exportable: true,
                exportTransformer: (item) => {
                    const language = this.getFirstAvailablePlatformValue(item, 'language')

                    return {
                        'Language': language && this.getLanguageFromCode(language) !== 'Unknown' ? this.getLanguageFromCode(language) : '',
                    }

                },
                enabled: () => {
                    return true
                },
                group: 'basic',
            })

            // Add global country column (from first available platform)
            tableColumns.push({
                id: 'global_country',
                key: 'global_country',
                header: {
                    label: 'Country',
                    is_sortable: false,
                    is_hideable: true,
                    class: '',
                },
                row: {
                    class: '',
                    formatter: ({ item }) => {
                        const country = this.getFirstAvailablePlatformValue(item, 'country')

                        return country ? this.getCountryFromCode(country) : ''
                    },
                },
                is_sticky: false,
                is_exportable: true,
                exportTransformer: (item) => ({
                    'Country': this.getFirstAvailablePlatformValue(item, 'country') ? this.getCountryFromCode(this.getFirstAvailablePlatformValue(item, 'country')) : '',
                }),
                enabled: () => {
                    return true
                },
                group: 'basic',
            })

            // Add global email column (from first available platform)
            tableColumns.push({
                id: 'email',
                key: 'email',
                header: {
                    label: 'Email',
                    is_sortable: false,
                    is_hideable: true,
                    class: '',
                },
                row: {
                    class: '',
                    slot: 'email',
                    slotTransformer: (item) => this.getFirstAvailablePlatformData(item, 'email'),
                },
                is_sticky: false,
                is_exportable: true,
                exportTransformer: (item) => ({
                    'Email': this.getFirstAvailablePlatformValue(item, 'email'),
                }),
                enabled: () => {
                    return true
                },
                group: 'basic',
            })

            tableColumns.push({
                id: 'global_gender',
                key: 'global_gender',
                header: {
                    label: 'Gender',
                    is_sortable: false,
                    is_hideable: true,
                    class: '',
                    tooltip: {
                        title: 'Gender',
                        content: [
                            {
                                text: 'This is an estimate made by AI to determine the gender of the creator.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ item }) => this.formatGenderValue(this.getFirstAvailablePlatformValue(item, 'gender')),
                },
                is_sticky: false,
                is_exportable: true,
                exportTransformer: (item) => ({
                    Gender: this.formatGenderValue(this.getFirstAvailablePlatformValue(item, 'gender')),
                }),
                enabled: () => {
                    return true
                },
                group: 'basic',
            })

            tableColumns.push({
                id: 'global_age_range',
                key: 'global_age_range',
                header: {
                    label: 'Age Range',
                    is_sortable: false,
                    is_hideable: true,
                    class: '',
                    tooltip: {
                        title: 'Age Range',
                        content: [
                            {
                                text: 'This is an estimate made by AI to determine the age range of the creator.',
                            },
                        ],
                    },
                },
                row: {
                    class: '',
                    formatter: ({ item }) => this.getFirstAvailablePlatformValue(item, 'age_range'),
                },
                is_sticky: false,
                is_exportable: true,
                exportTransformer: (item) => ({
                    'Age Range': this.getFirstAvailablePlatformValue(item, 'age_range'),
                }),
                enabled: () => {
                    return true
                },
                group: 'basic',
            })

            // Add platform-specific columns
            _.forEach(platforms, (platform) => {
                let index = 0

                // Platform creator name column
                tableColumns.push({
                    id: `platforms.${platform.key}.${index}.partialview.normalized.name`,
                    key: `platforms.${platform.key}.${index}.partialview.normalized.name`,
                    header: {
                        label: 'Creator',
                        sub_label: platform.label,
                        is_sortable: true,
                        is_hideable: false,
                        class: '',
                        tooltip: {
                            title: `${platform.label} Creator`,
                            sub_title: platform.label,
                            content: [
                                {
                                    text: `Creator profile and details for ${platform.label}.`,
                                },
                            ],
                        },
                    },
                    row: {
                        class: '',
                        slot: 'creator-small',
                        slotTransformer: (item) => ({
                            id: _.get(item, `platforms.${platform.key}[${index}].partialview._id`),
                            platform_id: `${platform.key}`,
                            name: _.get(item, `platforms.${platform.key}[${index}].partialview.normalized.name`),
                            display_name: _.get(
                                item,
                                `platforms.${platform.key}[${index}].partialview.normalized.display_name`,
                            ),
                            avatar_url: _.get(
                                item,
                                `platforms.${platform.key}[${index}].partialview.normalized.avatar_url`,
                            ),
                            logo: `${MEDIA_CDN_BASE}/${platform.key}/avatars/small/${_.get(item, 'platforms.' + platform.key + '[' + index + '].partialview._id')}`,
                        }),
                    },
                    is_sticky: false,
                    is_exportable: true,
                    exportTransformer: (item) => ({
                        [`${platform.label}`]: _.get(
                            item,
                            `platforms.${platform.key}[${index}].partialview.normalized.name`,
                        ),
                    }),
                    enabled: () => {
                        return true
                    },
                    group: platform.key,
                })

                // Platform metric columns
                _.forEach(platform.metrics, (metric) => {
                    const isTwitchViewerMetric = platform.key === 'twitch' && [
                        'metrics.streams.avg_viewers',
                        'metrics.streams.peak_viewers',
                        'metrics.streams.avg_chatters',
                    ].includes(metric.field)

                    let fieldFormatter = ({ field, item }) => {
                        if (
                            isTwitchViewerMetric &&
                            !_.get(item, `platforms.${platform.key}[${index}].metrics.streams.content_count`, 0)
                        ) {
                            return '-'
                        }

                        if (metric.formatter === 'formatEngagementRate') {
                            return this.formatEngagementRate(field)
                        }

                        if (field !== null && field !== undefined) {
                            if (metric.formatter === 'formatHours') {
                                return this.$filters.formatWholeNumberWithUnit(field / 60 / 60)
                            }
                            return this.$filters.formatWholeNumberWithUnit(field)
                        }
                        return metric.emptyValue ?? '-'
                    }

                    let exportFormatter = (item) => {
                        if (
                            isTwitchViewerMetric &&
                            !_.get(item, `platforms.${platform.key}[${index}].metrics.streams.content_count`, 0)
                        ) {
                            return {
                                [`${platform.label} ${metric.label}`]: null
                            }
                        }

                        let value = _.get(item, `platforms.${platform.key}[${index}].${metric.field}`)
                        if (metric.formatter === 'formatEngagementRate') {
                            value = this.formatEngagementRate(value, null)
                        }
                        else if (value && metric.formatter === 'formatHours') {
                            value = (value / 60 / 60).toFixed(1)
                        }
                        else if (value && metric.formatter === 'formatWholeNumberWithUnit') {
                            value = Math.round(value)
                        }
                        return {
                            [`${platform.label} ${metric.label}`]: value
                        }
                    }

                    tableColumns.push({
                        id: `platforms.${platform.key}.${index}.${metric.field}`,
                        key: `platforms.${platform.key}.${index}.${metric.field}`,
                        header: {
                            label: metric.label,
                            sub_label: `${platform.label} - ${metricWindowName}`,
                            is_sortable: true,
                            is_hideable: true,
                            class: '',
                            tooltip: {
                                title: metric.tooltip.title,
                                sub_title: `${platform.label} - ${metricWindowName}`,
                                content: [
                                    {
                                        text: metric.tooltip.content,
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: fieldFormatter,
                        },
                        is_sticky: false,
                        is_exportable: true,
                        exportTransformer: exportFormatter,
                        enabled: () => {
                            return true
                        },
                        group: platform.key,
                    })
                })

                tableColumns.push({
                    id: `platforms.${platform.key}.${index}.partialview.normalized.link`,
                    key: `platforms.${platform.key}.${index}.partialview.normalized.link`,
                    header: {
                        label: `${platform.label} Link`,
                        is_sortable: false,
                    },
                    header: {
                            label: 'Link',
                            sub_label: `${platform.label}`,
                            is_sortable: true,
                            is_hideable: true,
                            class: '',
                            tooltip: {
                                title: 'Link',
                                sub_title: `${platform.label}`,
                            },
                        },
                    row: {
                        class: '',
                        slot: 'platform-link',
                        slotTransformer: (item) => ({
                            platform_id: `${platform.key}`,
                            id: _.get(item, `platforms.${platform.key}[${index}].partialview._id`),
                            name: _.get(
                                item,
                                `platforms.${platform.key}[${index}].partialview.normalized.name`,
                            ),
                            link :  _.get(item, `platforms.${platform.key}[${index}].partialview.normalized.link`),
                        } ),

                    },
                    is_exportable: true,
                    exportTransformer: (item) => ({
                        [`${platform.label} Link`]: _.get(
                            item,
                            `platforms.${platform.key}.${index}.partialview.normalized.link`,
                            '',
                        ),
                    }),
                    enabled: () => {
                        return true
                    },
                    group: platform.key,
                })
            })

            this.table.columns = tableColumns
            if (this.preferences?.tables?.listreportcombined && this.preferences?.tables?.listreportcombined?.columns) {
                this.table.enabledColumns = this.preferences.tables.listreportcombined.columns
            } else {
                // Default to showing creator column and basic platform columns
                this.table.enabledColumns = ['creator'].concat(
                    _.map(platforms, platform => `platforms.${platform.key}.0.partialview.normalized.name`)
                )
            }
        },

        reset() {
            this.paging.currentPage = 1
            this.creators = []
            this.state.hasGenerated = false
            this.state.isLoaded = false
            this.createColumns() // Regenerate columns to update metric window labels
            this.debounceGenerate()
        },

        debounceGenerate: _.debounce(function () {
            nextTick(() => {
                this.onGenerateReport(false, false)
            })
        }, 250),

        onChangeSort(sortCol) {
            if (sortCol === this.paging.sort.sortBy) {
                if (this.paging.sort.sortDir === 'asc') {
                    this.paging.sort.sortDir = ''
                    this.paging.sort.sortBy = ''
                } else {
                    this.paging.sort.sortDir = 'asc'
                }
            } else {
                this.paging.sort.sortBy = sortCol
                this.paging.sort.sortDir = 'desc'
            }
        },

        async onGenerateReport(isExport, preExport) {
            if (!this.list || !this.list.id) {
                return null
            }

            if (!isExport) {
                this.state.hasGenerated = true
                this.state.isLoaded = false
            }

            const options = {}
            const filters = []

            options.contentType = this.filters.contentType
            options.dayRange = this.filters.dayRange
            options.combined = true

            if (this.searchText) {
                filters.push({ id: 'primary.partialview.normalized.display_name', value: this.searchText })
            }

            this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_REGEX', '^/lists/[^/]+/report$')

            return reportsAPI
                .getListReport(this.$axios, this.list.id, null, this.paging, options, filters, isExport, preExport)
                .then((response) => {
                    if (response.isQuotaError) {
                        return { isQuotaError: response.isQuotaError }
                    }

                    if (isExport) {
                        return response.value
                    }

                    if (!response.success) return false

                    const report = response.value

                    if (this.list.id !== report?.listId) {
                        return false
                    }

                    this.creators = []

                    if (report.items) {
                        this.creators = _.map(report.items, (creator) => {
                            creator.primary = _.find(creator.platforms[creator.primary.platform_id], (platformView) => {
                                return platformView.partialview._id === creator.primary.platform_user_id
                            })

                            return creator
                        })
                    }

                    this.paging.totalRecords = report.paging.totalRecords
                    this.paging.totalPages = report.paging.totalPages
                    this.paging.currentPage = report.paging.currentPage

                    this.state.isLoaded = true

                    return true
                })
        },

        async onFilterChange(updatedSort) {
            _.assign(this.paging.sort, updatedSort)
            await this.onGenerateReport(false, false)
        },

        async onPagingChange(updatedPaging) {
            _.assign(this.paging, updatedPaging)
            await this.onGenerateReport(false, false)
        },

        onEnabledColumnChange(newEnabledColumns) {
            this.table.enabledColumns = newEnabledColumns
            this.$store.commit('preferences:set', {
                key: 'tables.listreportcombined.columns',
                value: this.table.enabledColumns,
            })
        },

        onExportCreators() {
            this.$store.commit('modal:setData', {
                modal_id: 'export_table',
                data: {
                    is_open: true,
                    file_name: `${this.list?.name || 'creators'} - creators - ${this.filters.dayRange} day`,
                    columns: {
                        all: this.table.columns,
                        enabled: this.getDefaultExportColumns(),
                    },
                    checkerFn: async () => {
                        const reportResponse = await this.onGenerateReport(true, true)

                        if (reportResponse?.isQuotaError) {
                            return -1
                        }

                        const { items } = reportResponse

                        const creators = _.map(items, (creator) => {
                            // Get first available platform data for email checking
                            const platformData = this.getFirstAvailablePlatformData(creator)
                            return ['superview', platformData.platform_id, platformData.platform_user_id]
                        }).filter(creator => creator.platform_id && creator.platform_user_id)

                        const statusChecks = await this.$store.dispatch('bulkFetchRevealStatus', creators)
                        const revealable = _.filter(statusChecks, (item) => {
                            return (
                                item?.payload?.is_revealable === true && item?.payload?.is_previously_unlocked === false
                            )
                        })
                        return revealable.length
                    },
                    rowCount: this.paging.totalRecords,
                    providerFn: async () => {
                        const reportResponse = await this.onGenerateReport(true, false)
                        // Transform items to include platform_id and id for email reveal process
                        return _.map(reportResponse.items, (item) => {
                            const platformData = this.getFirstAvailablePlatformData(item)
                            return {
                                ...item,
                                platform_id: platformData.platform_id,
                                id: platformData.platform_user_id,
                            }
                        })
                    },
                },
            })
        },

        onHeaderColumnAction() {},
    },
}
</script>
